import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import type { Tone } from '@buildiary/core';
import { useT } from '../app/context';
import { errorText, fileSrc, rememberFile, upload } from '../lib/api';
import { isNative, nativePhoto } from '../lib/native';
import { Icon, type IconName } from './Icon';

// ---------------------------------------------------------------- buttons

type BtnVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'success';
export function Button({
  variant = 'secondary',
  size,
  icon,
  loading,
  block,
  children,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: BtnVariant; size?: 'sm' | 'lg'; icon?: IconName; loading?: boolean; block?: boolean }) {
  const cls = ['btn', variant !== 'secondary' && variant, size, block && 'block', !children && 'icon-only', className].filter(Boolean).join(' ');
  return (
    <button type="button" className={cls} disabled={loading || rest.disabled} aria-busy={loading || undefined} {...rest}>
      {loading ? <span className="spinner" aria-hidden="true" /> : icon ? <Icon name={icon} size={size === 'sm' ? 15 : 16} stroke={2} /> : null}
      {children}
    </button>
  );
}

export function LinkButton({ to, variant = 'secondary', size, icon, children, className, external }: { to: string; variant?: BtnVariant; size?: 'sm' | 'lg'; icon?: IconName; children?: ReactNode; className?: string; external?: boolean }) {
  const cls = ['btn', variant !== 'secondary' && variant, size, className].filter(Boolean).join(' ');
  const inner = (
    <>
      {icon && <Icon name={icon} size={size === 'sm' ? 15 : 16} stroke={2} />}
      {children}
    </>
  );
  if (external) return <a className={cls} href={to} target="_blank" rel="noreferrer">{inner}</a>;
  return <Link className={cls} to={to}>{inner}</Link>;
}

// ---------------------------------------------------------------- chips, avatars, bars

const WARN_ICON: IconName = 'warn';
/** A status chip. Warning chips always carry the warning icon (bronze sits close to amber). */
export function Chip({ tone = 'neutral', icon, size, children, className }: { tone?: Tone; icon?: IconName | null; size?: 'sm' | 'lg'; children: ReactNode; className?: string }) {
  const ic = icon === null ? null : icon ?? (tone === 'warning' ? WARN_ICON : undefined);
  return (
    <span className={`chip tone-${tone}${size ? ' ' + size : ''}${className ? ' ' + className : ''}`}>
      {ic && <Icon name={ic} size={12} stroke={2.4} />}
      {children}
    </span>
  );
}

export function Avatar({ initials, size = 36, soft, square, className }: { initials: string; size?: number; soft?: boolean; square?: boolean; className?: string }) {
  return (
    <span className={`avatar${soft ? ' soft' : ''}${square ? ' sq' : ''}${className ? ' ' + className : ''}`} style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }} aria-hidden="true">
      {initials}
    </span>
  );
}

export function Bar({ pct, tone, lg, label }: { pct: number; tone?: 'danger' | 'success' | 'muted'; lg?: boolean; label?: string }) {
  const bg = tone === 'danger' ? 'var(--danger)' : tone === 'success' ? 'var(--success)' : tone === 'muted' ? 'var(--brand-300)' : undefined;
  const w = Math.max(0, Math.min(100, pct));
  return (
    <div className={`bar${lg ? ' lg' : ''}`} role="progressbar" aria-valuenow={Math.round(w)} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
      <i style={{ width: `${w}%`, background: bg }} />
    </div>
  );
}

export function ProgressCell({ pct }: { pct: number }) {
  return (
    <div className="progress-cell">
      <Bar pct={pct} />
      <span className="pct">{Math.round(pct)}%</span>
    </div>
  );
}

export function Spark({ points, w = 70, h = 26 }: { points?: number[]; w?: number; h?: number }) {
  if (!points || points.length < 2) return null;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min || 1;
  const pts = points.map((v, i) => `${((i / (points.length - 1)) * w).toFixed(1)},${(h - 3 - ((v - min) / span) * (h - 6)).toFixed(1)}`).join(' ');
  return (
    <svg className="spark" width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true" style={{ direction: 'ltr' }}>
      <polyline points={pts} fill="none" stroke="var(--brand-500)" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

const TONE_BG: Record<Tone, string> = { brand: 'tone-brand', success: 'tone-success', warning: 'tone-warning', danger: 'tone-danger', neutral: 'tone-neutral' };
export function IconChip({ icon, tone = 'brand', size }: { icon: IconName; tone?: Tone | 'solid'; size?: 'sm' | 'md' }) {
  const cls = tone === 'solid' ? 'tone-solid' : TONE_BG[tone];
  return (
    <span className={`icon-chip ${cls}${size ? ' ' + size : ''}`} aria-hidden="true">
      <Icon name={icon} size={size === 'sm' ? 16 : 18} />
    </span>
  );
}

// ---------------------------------------------------------------- tiles

export function Tile({ title, value, sub, icon, tone = 'brand', spark, to, ccy, compact }: { title: string; value: ReactNode; sub?: ReactNode; icon?: IconName; tone?: Tone; spark?: number[]; to?: string; ccy?: string; compact?: boolean }) {
  const body = (
    <>
      <div className="tile-top">
        <span className="tile-title">{title}</span>
        {icon && <IconChip icon={icon} tone={tone} size="md" />}
      </div>
      <div className="tile-mid">
        <span className="tile-value">
          {ccy && <span className="ccy">{ccy} </span>}
          {value}
        </span>
        {spark && <Spark points={spark} />}
      </div>
      {sub !== undefined && <span className="tile-sub">{sub}</span>}
    </>
  );
  const cls = `tile${compact ? ' compact' : ''}`;
  return to ? <Link to={to} className={cls}>{body}</Link> : <div className={cls}>{body}</div>;
}

// ---------------------------------------------------------------- cards and page head

export function Card({ title, sub, actions, children, className, pad, headClass, id }: { title?: ReactNode; sub?: ReactNode; actions?: ReactNode; children?: ReactNode; className?: string; pad?: boolean; headClass?: string; id?: string }) {
  return (
    <section className={`card${pad ? ' pad' : ''}${className ? ' ' + className : ''}`} id={id} aria-label={typeof title === 'string' ? title : undefined}>
      {(title || actions) && !pad && (
        <div className={`card-head${headClass ? ' ' + headClass : ''}`}>
          <div className="stack" style={{ gap: 2 }}>
            {title && <h2 className="card-title">{title}</h2>}
            {sub && <span className="card-sub">{sub}</span>}
          </div>
          {actions && <div className="row-wrap">{actions}</div>}
        </div>
      )}
      {(title || actions) && pad && (
        <div className="row between" style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div className="stack" style={{ gap: 2 }}>
            {title && <h2 className="card-title">{title}</h2>}
            {sub && <span className="card-sub">{sub}</span>}
          </div>
          {actions && <div className="row-wrap">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export function PageHead({ eyebrow, title, sub, actions, back }: { eyebrow?: ReactNode; title: ReactNode; sub?: ReactNode; actions?: ReactNode; back?: { to: string; label: string } }) {
  return (
    <div className="page-head">
      <div className="stack">
        {back && (
          <Link className="back-link" to={back.to}>
            <Icon name="chevronLeft" size={16} className="flip-rtl" />
            {back.label}
          </Link>
        )}
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <h1 className="page-title">{title}</h1>
        {sub && <span className="page-sub">{sub}</span>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </div>
  );
}

// ---------------------------------------------------------------- states

export function EmptyState({ icon = 'box', title, text, action }: { icon?: IconName; title: string; text?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <IconChip icon={icon} tone="neutral" />
      <h3>{title}</h3>
      {text && <p>{text}</p>}
      {action}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const t = useT();
  return (
    <div className="empty" role="alert">
      <IconChip icon="warn" tone="danger" />
      <h3>{t('c.error')}</h3>
      <p>{errorText(error)}</p>
      {onRetry && <Button onClick={onRetry} icon="refresh">{t('c.retry')}</Button>}
    </div>
  );
}

export function Loading({ rows = 4 }: { rows?: number }) {
  const t = useT();
  return (
    <div className="loading-block" aria-busy="true" aria-label={t('c.loading')}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skel" style={{ height: i === 0 ? 22 : 16, width: i === 0 ? '40%' : `${90 - i * 9}%` }} />
      ))}
    </div>
  );
}

/** Renders loading, error or the content for a useAsync result. */
export function Async<T>({ state, children, rows }: { state: { data: T | null; error: unknown; loading: boolean; reload: () => void }; children: (data: T) => ReactNode; rows?: number }) {
  if (state.error && !state.data) return <ErrorState error={state.error} onRetry={state.reload} />;
  if (!state.data) return <Loading rows={rows} />;
  return <>{children(state.data)}</>;
}

// ---------------------------------------------------------------- tables

export type Column<R> = { key: string; label: ReactNode; align?: 'end'; className?: string; render: (row: R) => ReactNode; width?: number | string; minor?: boolean };

export function DataTable<R>({ columns, rows, rowKey, onRow, empty, selected, foot, minWidth = 640, label }: { columns: Column<R>[]; rows: R[]; rowKey: (r: R) => string; onRow?: (r: R) => void; empty?: ReactNode; selected?: string | null; foot?: ReactNode; minWidth?: number; label?: string }) {
  if (rows.length === 0 && empty) return <>{empty}</>;
  return (
    <div className="table-wrap">
      <table className="table" style={{ minWidth }} aria-label={label}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} scope="col" className={[c.align === 'end' && 'r', c.minor && 'minor'].filter(Boolean).join(' ') || undefined} style={c.width ? { width: c.width } : undefined}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const k = rowKey(r);
            return (
              <tr
                key={k}
                className={[onRow && 'click', selected === k && 'selected'].filter(Boolean).join(' ') || undefined}
                onClick={onRow ? () => onRow(r) : undefined}
                onKeyDown={onRow ? (e) => { if (e.key === 'Enter') onRow(r); } : undefined}
                tabIndex={onRow ? 0 : undefined}
              >
                {columns.map((c) => (
                  <td key={c.key} className={[c.align === 'end' && 'r', c.className, c.minor && 'minor'].filter(Boolean).join(' ') || undefined}>
                    {c.render(r)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
        {foot && <tfoot>{foot}</tfoot>}
      </table>
    </div>
  );
}

// ---------------------------------------------------------------- facets and search

export type Facet = { value: string; label: string; n?: number };
export function FacetChips({ label, options, value, onChange }: { label: string; options: Facet[]; value: string; onChange: (v: string) => void }) {
  return (
    <div className="facet-row" role="group" aria-label={label}>
      <span className="facet-label">{label}</span>
      <div className="facet-chips">
        {options.map((o) => (
          <button key={o.value} type="button" className="facet" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
            {o.label}
            {o.n !== undefined && <span className="n">{o.n}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

export function SearchBox({ value, onChange, placeholder, label }: { value: string; onChange: (v: string) => void; placeholder: string; label?: string }) {
  return (
    <label className="search-box">
      <Icon name="search" size={16} stroke={2} style={{ color: 'var(--ink-5)' }} />
      <input type="search" value={value} placeholder={placeholder} aria-label={label ?? placeholder} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

export function Seg<V extends string>({ value, options, onChange, label }: { value: V; options: { value: V; label: ReactNode }[]; onChange: (v: V) => void; label: string }) {
  return (
    <div className="seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Tabs<V extends string>({ value, options, onChange, label }: { value: V; options: { value: V; label: ReactNode }[]; onChange: (v: V) => void; label: string }) {
  return (
    <div className="tabs" role="tablist" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" role="tab" aria-selected={o.value === value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- form fields

export function Field({ label, hint, error, children, className, required }: { label: ReactNode; hint?: ReactNode; error?: string | null; children: ReactNode; className?: string; required?: boolean }) {
  return (
    <label className={`field${className ? ' ' + className : ''}`}>
      <span>
        {label}
        {required && <span aria-hidden="true" className="danger-text"> *</span>}
      </span>
      {children}
      {hint && !error && <span className="hint">{hint}</span>}
      {error && (
        <span className="err" role="alert">
          <Icon name="warn" size={14} stroke={2.2} />
          {error}
        </span>
      )}
    </label>
  );
}
export function Input({ className, invalid, ...rest }: InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }) {
  return <input className={`input${className ? ' ' + className : ''}`} aria-invalid={invalid || undefined} {...rest} />;
}
export function Select({ className, invalid, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }) {
  return (
    <select className={`select${className ? ' ' + className : ''}`} aria-invalid={invalid || undefined} {...rest}>
      {children}
    </select>
  );
}
export function Textarea({ className, invalid, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }) {
  return <textarea className={`textarea${className ? ' ' + className : ''}`} aria-invalid={invalid || undefined} {...rest} />;
}
export function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <span className="toggle">
      <input type="checkbox" role="switch" checked={checked} aria-label={label} onChange={(e) => onChange(e.target.checked)} />
      <span />
    </span>
  );
}

// ---------------------------------------------------------------- photos

/** A stored photo, or a bronze placeholder with a caption when there is no file to show (demo data). */
export function Photo({ id, label, height = 84, width, className }: { id?: string | null; label: string; height?: number | string; width?: number | string; className?: string }) {
  const src = fileSrc(id);
  const [broken, setBroken] = useState(false);
  return (
    <div className={`ph${className ? ' ' + className : ''}`} style={{ height, width }} role="img" aria-label={label}>
      <span>[{label}]</span>
      {src && !broken && <img src={src} alt="" loading="lazy" onError={() => setBroken(true)} />}
    </div>
  );
}

/**
 * A file input for photos. In the Buildiary Android app (1.1's bridge) a camera photo comes from the app's live
 * camera instead; in a browser it is the usual file input (camera on phones).
 */
export function FilePick({ accept, capture, multiple, onFiles, title, style }: { accept: string; capture?: boolean; multiple?: boolean; onFiles: (files: File[]) => void; title: string; style?: CSSProperties }) {
  if (isNative() && capture && accept.startsWith('image')) {
    return (
      <button type="button" aria-label={title} style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', border: 0, background: 'transparent', ...style }}
        onClick={async () => {
          try {
            const ph = await nativePhoto(title, 'back');
            if (ph) onFiles([ph.file]);
          } catch {
            /* the app could not open the camera */
          }
        }} />
    );
  }
  return <input type="file" accept={accept} capture={capture ? 'environment' : undefined} multiple={multiple} style={style} onChange={(e) => { onFiles(Array.from(e.target.files ?? [])); e.target.value = ''; }} />;
}

/** Takes photos with the camera (or picks files), uploads each through POST /api/files and returns the file ids. */
export function PhotoPicker({ value, onChange, label, max = 6, accept = 'image/*', capture = true }: { value: string[]; onChange: (ids: string[]) => void; label: string; max?: number; accept?: string; capture?: boolean }) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const pick = async (files: File[] | null) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    setErr(null);
    try {
      const ids: string[] = [];
      for (const f of Array.from(files).slice(0, max - value.length)) {
        const ref = await upload(f);
        rememberFile(ref.id, URL.createObjectURL(f));
        ids.push(ref.id);
      }
      onChange([...value, ...ids]);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="stack" style={{ gap: 6 }}>
      <div className="photo-pick">
        {value.map((id, i) => (
          <div key={id} className="photo-thumb">
            {fileSrc(id) ? <img src={fileSrc(id) ?? ''} alt={`${label} ${i + 1}`} /> : <span>{t('c.photo')} {i + 1}</span>}
            <button type="button" className="btn sm icon-only" style={{ position: 'absolute', top: 2, insetInlineEnd: 2, minHeight: 26, width: 26, padding: 0, zIndex: 1 }} aria-label={`${t('c.delete')} ${i + 1}`} onClick={() => onChange(value.filter((x) => x !== id))}>
              <Icon name="x" size={13} stroke={2.4} />
            </button>
          </div>
        ))}
        {value.length < max && (
          <label className="photo-add" aria-label={label}>
            {busy ? <span className="spinner" /> : <Icon name="camera" size={26} stroke={1.9} />}
            <FilePick accept={accept} capture={capture} multiple={max > 1} title={label} onFiles={(f) => void pick(f)} />
          </label>
        )}
      </div>
      {err && <span className="err danger-text small" role="alert">{err}</span>}
    </div>
  );
}

// ---------------------------------------------------------------- overlays

function useEscape(onClose: () => void) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
}
function useFocusFirst<E extends HTMLElement>() {
  const ref = useRef<E | null>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const el = ref.current;
    const first = el?.querySelector<HTMLElement>('[data-autofocus], input:not([type=hidden]), select, textarea, button:not(.dialog-x)');
    (first ?? el)?.focus();
    return () => prev?.focus?.();
  }, []);
  return ref;
}

export function Dialog({ title, sub, onClose, children, footer, wide, label }: { title: ReactNode; sub?: ReactNode; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean; label?: string }) {
  const t = useT();
  useEscape(onClose);
  const ref = useFocusFirst<HTMLDivElement>();
  const id = useId();
  return createPortal(
    <div className="overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`dialog${wide ? ' wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby={id} aria-label={label} ref={ref} tabIndex={-1}>
        <div className="dialog-head">
          <div className="stack grow">
            <h2 id={id}>{title}</h2>
            {sub && <span className="muted small">{sub}</span>}
          </div>
          <button type="button" className="btn ghost icon-only dialog-x" aria-label={t('c.close')} onClick={onClose}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div className="dialog-body">{children}</div>
        {footer && <div className="dialog-foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

export function Sheet({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  useEscape(onClose);
  const ref = useFocusFirst<HTMLDivElement>();
  return createPortal(
    <div className="overlay sheet-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sheet" role="dialog" aria-modal="true" aria-label={title} ref={ref} tabIndex={-1}>
        <div className="sheet-grip" aria-hidden="true" />
        <h2 style={{ fontSize: 17, marginBottom: 12 }}>{title}</h2>
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function Dropdown({ button, label, children, align = 'end', className }: { button: ReactNode; label: string; children: (close: () => void) => ReactNode; align?: 'start' | 'end'; className?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', h);
    document.addEventListener('keydown', k);
    return () => {
      document.removeEventListener('mousedown', h);
      document.removeEventListener('keydown', k);
    };
  }, [open]);
  return (
    <div className="dd" ref={ref}>
      <button type="button" className={className ?? 'tb-btn'} aria-label={label} aria-haspopup="true" aria-expanded={open} onClick={() => setOpen(!open)}>
        {button}
      </button>
      {open && <div className={`dd-menu${align === 'start' ? ' start' : ''}`}>{children(() => setOpen(false))}</div>}
    </div>
  );
}

// ---------------------------------------------------------------- toasts

type ToastKind = 'info' | 'success' | 'error';
type ToastItem = { id: number; text: string; kind: ToastKind };
const ToastContext = createContext<(text: string, kind?: ToastKind) => void>(() => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((text: string, kind: ToastKind = 'success') => {
    const id = Date.now() + Math.random();
    setItems((xs) => [...xs, { id, text, kind }]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), 4200);
  }, []);
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {items.map((i) => (
          <div key={i.id} className={`toast ${i.kind}`}>
            <Icon name={i.kind === 'error' ? 'warn' : 'tick'} size={16} stroke={2.2} />
            {i.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
export function useToast() {
  return useContext(ToastContext);
}

// ---------------------------------------------------------------- generic form dialog

export type FormField =
  | { name: string; label: string; type?: 'text' | 'number' | 'date' | 'tel' | 'email' | 'password'; required?: boolean; hint?: string; placeholder?: string; min?: number; step?: string; span?: boolean; inputMode?: 'numeric' | 'decimal' | 'text' | 'tel' }
  | { name: string; label: string; type: 'select'; options: { value: string; label: string }[]; required?: boolean; hint?: string; span?: boolean }
  | { name: string; label: string; type: 'textarea'; required?: boolean; hint?: string; placeholder?: string; span?: boolean }
  | { name: string; label: string; type: 'photos'; required?: boolean; hint?: string; max?: number; span?: boolean }
  | { name: string; label: string; type: 'check'; hint?: string; span?: boolean };

export type FormValues = Record<string, string | string[] | boolean>;

/** A dialog with labelled fields, required-field checks, server field errors and a busy state. */
export function FormDialog({ title, sub, fields, initial, submitLabel, onSubmit, onClose, wide, intro }: { title: string; sub?: string; fields: FormField[]; initial?: FormValues; submitLabel: string; onSubmit: (v: FormValues) => Promise<void>; onClose: () => void; wide?: boolean; intro?: ReactNode }) {
  const t = useT();
  const [v, setV] = useState<FormValues>(() => {
    const o: FormValues = {};
    for (const f of fields) o[f.name] = initial?.[f.name] ?? (f.type === 'photos' ? [] : f.type === 'check' ? false : f.type === 'select' && f.required ? f.options[0]?.value ?? '' : '');
    return o;
  });
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);
  const set = (k: string, val: string | string[] | boolean) => setV((o) => ({ ...o, [k]: val }));
  const submit = async () => {
    const e: Record<string, string> = {};
    for (const f of fields) {
      const val = v[f.name];
      if ('required' in f && f.required && (val === '' || (Array.isArray(val) && val.length === 0))) e[f.name] = f.type === 'photos' ? 'Add a photo.' : `${f.label}: ${t('c.required').toLowerCase()}.`;
      if (f.type === 'number' && val !== '' && Number.isNaN(Number(val))) e[f.name] = 'Type a number.';
      if (f.type === 'number' && f.min !== undefined && val !== '' && Number(val) < f.min) e[f.name] = `At least ${f.min}.`;
    }
    setErrs(e);
    if (Object.keys(e).length) return;
    setBusy(true);
    setFormErr(null);
    try {
      await onSubmit(v);
    } catch (err) {
      const fe = (err as { body?: { fields?: Record<string, string> } }).body?.fields;
      if (fe) setErrs(fe);
      setFormErr(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={title}
      sub={sub}
      onClose={onClose}
      wide={wide}
      footer={
        <>
          <Button onClick={onClose}>{t('c.cancel')}</Button>
          <Button variant="primary" loading={busy} onClick={() => void submit()}>{submitLabel}</Button>
        </>
      }
    >
      <form className="form" onSubmit={(e) => { e.preventDefault(); void submit(); }} noValidate>
        {intro}
        {formErr && <div className="alert danger" role="alert"><Icon name="warn" size={16} />{formErr}</div>}
        <div className="form-grid">
          {fields.map((f) => {
            const err = errs[f.name];
            const cls = f.span || f.type === 'textarea' || f.type === 'photos' || f.type === 'check' ? 'span-2' : undefined;
            if (f.type === 'check')
              return (
                <label key={f.name} className={`check ${cls}`}>
                  <input type="checkbox" checked={Boolean(v[f.name])} onChange={(e) => set(f.name, e.target.checked)} />
                  <span className="stack" style={{ gap: 2 }}>
                    <span>{f.label}</span>
                    {f.hint && <span className="muted small">{f.hint}</span>}
                  </span>
                </label>
              );
            if (f.type === 'photos')
              return (
                <div key={f.name} className={`field ${cls}`}>
                  <span>{f.label}{f.required && <span className="danger-text"> *</span>}</span>
                  <PhotoPicker value={(v[f.name] as string[]) ?? []} onChange={(ids) => set(f.name, ids)} label={f.label} max={f.max ?? 4} />
                  {f.hint && !err && <span className="hint">{f.hint}</span>}
                  {err && <span className="err" role="alert"><Icon name="warn" size={14} />{err}</span>}
                </div>
              );
            return (
              <Field key={f.name} label={f.label} hint={f.hint} error={err} className={cls} required={'required' in f && f.required}>
                {f.type === 'select' ? (
                  <Select value={v[f.name] as string} invalid={!!err} onChange={(e) => set(f.name, e.target.value)}>
                    {!f.required && <option value="">—</option>}
                    {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </Select>
                ) : f.type === 'textarea' ? (
                  <Textarea value={v[f.name] as string} invalid={!!err} placeholder={f.placeholder} onChange={(e) => set(f.name, e.target.value)} />
                ) : (
                  <Input
                    type={f.type === 'number' ? 'text' : f.type ?? 'text'}
                    inputMode={f.type === 'number' ? f.inputMode ?? 'decimal' : f.inputMode}
                    className={f.type === 'number' ? 'mono' : undefined}
                    value={v[f.name] as string}
                    invalid={!!err}
                    placeholder={f.placeholder}
                    onChange={(e) => set(f.name, e.target.value)}
                  />
                )}
              </Field>
            );
          })}
        </div>
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}

export function Confirm({ title, text, confirmLabel, danger, onConfirm, onClose, children }: { title: string; text?: ReactNode; confirmLabel: string; danger?: boolean; onConfirm: () => Promise<void> | void; onClose: () => void; children?: ReactNode }) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <Dialog
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('c.cancel')}</Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            loading={busy}
            onClick={async () => {
              setBusy(true);
              setErr(null);
              try {
                await onConfirm();
              } catch (e) {
                setErr(errorText(e));
                setBusy(false);
              }
            }}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {text && <p>{text}</p>}
      {children}
      {err && <div className="alert danger" role="alert">{err}</div>}
    </Dialog>
  );
}

/** Small helper: numbers from form values. */
export function n(v: string | string[] | boolean | undefined): number {
  return typeof v === 'string' && v.trim() !== '' ? Number(v.replace(/,/g, '')) : 0;
}
export function s(v: string | string[] | boolean | undefined): string {
  return typeof v === 'string' ? v.trim() : '';
}
export function ids(v: string | string[] | boolean | undefined): string[] {
  return Array.isArray(v) ? v : [];
}
